//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import {
  BodyText,
  LinkPrimaryText,
  MetatagText,
} from '@components/atoms/text/Text';
import UserImage from '@images/icons/user.svg';
import { SocialNetwork } from '@models/SocialNetwork';
import { Mixins } from '@theme/mixins';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { getUserHref } from '@uia/details/utils/socialNetworkUtils';
import { isDefined } from '@utils/filterPredicates';
import { withTracking } from '@utils/withTracking';

export interface TaggedUser {
  id?: string;
  username?: string;
}

export interface TaggedUsersProps {
  isRouteToExternal: boolean;
  socialNetwork: SocialNetwork;
  taggedUsers: TaggedUser[];
}

export interface TaggedUserLinkProps {
  href: string;
  isRouteToExternal: boolean;
  title: string;
}

const TaggedUserLink = ({
  href,
  isRouteToExternal,
  title,
}: TaggedUserLinkProps) => (
  <LinkPrimaryText>
    <Link
      href={href}
      trackingId={UIATrackingIds.DETAILS_OPEN_TAGGED_USER}
      {...(isRouteToExternal ? { rel: 'noreferrer', target: '_blank' } : {})}
    >
      {title}
    </Link>
  </LinkPrimaryText>
);

export const TaggedUsers = ({
  isRouteToExternal,
  socialNetwork,
  taggedUsers,
}: TaggedUsersProps) => (
  <>
    {taggedUsers.length > 0 && (
      <Wrapper>
        <Title>
          <Image alt='Tagged User' src={UserImage} />
          Tagged Users
        </Title>
        <Users>
          {taggedUsers.map((user, index) => {
            const socialNetworkId =
              isRouteToExternal && socialNetwork === SocialNetwork.Facebook
                ? user.id
                : user.username;
            const href = getUserHref(
              isRouteToExternal,
              socialNetwork,
              socialNetworkId,
            );
            const title = `${user.username}${index + 1 === taggedUsers.length ? '' : ', '}`;

            return (
              <li key={user.username}>
                {isDefined(href) ? (
                  <TaggedUserLink
                    href={href}
                    isRouteToExternal={isRouteToExternal}
                    title={title}
                  />
                ) : (
                  <BodyText>{title}</BodyText>
                )}
              </li>
            );
          })}
        </Users>
      </Wrapper>
    )}
  </>
);

TaggedUsers.TaggedUserLink = TaggedUserLink;

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    padding: ${theme.spacings[3]} ${theme.spacings[0]};
    gap: ${theme.spacings[2]};
  `,
);

const Title = styled(MetatagText)(
  ({ theme }) => `
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${theme.spacings[1]};
  `,
);

const Image = styled.img(
  ({ theme }) => `
    height: ${theme.spacings[3]};
    width: ${theme.spacings[3]};
    opacity: 0.4;
  `,
);

const Users = styled.ul(
  ({ theme }) => `
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${theme.spacings[2]};
    margin: ${theme.spacings[0]};
    padding: ${theme.spacings[0]};
    list-style: none;
  `,
);

const Link = withTracking(
  styled.a(
    ({ theme }) => `
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: ${theme.spacings[2]};
  
      && {
        ${Mixins.Text.BodyBold}
        color: ${theme.colors.black};
        cursor: pointer;
  
        :hover {
          text-decoration: none;
        }
      }
    `,
  ),
);
